import { useState, useEffect, useMemo, useCallback, useRef } from 'react'
import { api } from '../../lib/api'
import { useToast } from '../ui/Toast'
import { Button } from '../ui/Button'
import { TYPE_COLORS, getDeviceModule, type PortConnection } from './deviceModules'
import { getStatusColor, isStatusOnline, buildPortsMap, type PortMapEntry } from '../../lib/portMapping'
import { getModelForDevice, makeUniFiSwitchModule, PORT_CONFIGS } from '../rack/UniFiSwitchModule'

const PlusIcon = <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round"><path d="M12 5v14M5 12h14"/></svg>

// ── Types ────────────────────────────────────────────────

interface RackDevice {
  id: string
  device_id: string
  name: string
  model: string
  mpn: string | null
  type: string
  start_u: number
  size_u: number
  status: string
  ip?: string
  uptime?: string
  ports?: number
  poeBudget?: number
  storage?: string
  load?: string
  runtime?: string
}

interface Rack {
  id: string
  slug: string
  name: string
  location: string
  total_u: number
  devices: RackDevice[]
}

interface DeviceInterface {
  id: string
  device_id: string
  name: string
  port_number: number
  connected_device_id?: string
  connected_device_name?: string
  connected_device_type?: string
  connected_port?: number
  cable_id?: string
  ip?: string
  vlan?: number
  speed?: string
  poe_draw?: number
  status?: string
}

interface RackConnection {
  ppPort: number
  swId: string
  swPort: number
  cable: string
  device: string
  deviceName: string
  deviceType: string
  ip: string
  vlan?: number
  speed?: string
  poe?: number
  offline?: boolean
}

interface SiteRackViewProps {
  siteId: string
  onSwitchToNetwork?: (switchId: string) => void
}

// ── Constants ────────────────────────────────────────────

const U_H = 48
const RACK_W = 500
const DEVICE_X = 30
const DEVICE_W = RACK_W - 60

// ── Helper ───────────────────────────────────────────────

function rnd(min: number, max: number): number {
  return Math.floor(Math.random() * (max - min + 1)) + min
}

// ── Main Component ───────────────────────────────────────

export function SiteRackView({ siteId, onSwitchToNetwork }: SiteRackViewProps) {
  const { toast } = useToast()
  const [racks, setRacks] = useState<Rack[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [connections, setConnections] = useState<RackConnection[]>([])
  const [portsMaps, setPortsMaps] = useState<Record<string, Record<number, PortMapEntry>>>({})

  const [face, setFace] = useState<'front' | 'back'>('front')
  const [zoomed, setZoomed] = useState<string | null>(null)
  const [hovConn, setHovConn] = useState<RackConnection | null>(null)
  const [selConn, setSelConn] = useState<RackConnection | null>(null)
  const [hovDevice, setHovDevice] = useState<string | null>(null)
  const [hovPort, setHovPort] = useState<{ deviceId: string; portNum: number | string } | null>(null)
  const [portTooltip, setPortTooltip] = useState<{ portNum: number; x: number; y: number; conn: RackConnection | null; entry: PortMapEntry | null } | null>(null)
  const svgOverlayRef = useRef<SVGSVGElement | null>(null)
  const [animating, setAnimating] = useState(false)
  const [showEditRack, setShowEditRack] = useState(false)
  const [editRackForm, setEditRackForm] = useState({ name: '', total_u: 42, type: 'freestanding', location: '' })
  const [editRackSaving, setEditRackSaving] = useState(false)
  const [showAddRack, setShowAddRack] = useState(false)
  const [addRackForm, setAddRackForm] = useState({ name: '', total_u: 42, type: 'freestanding', location: '' })
  const [addRackSaving, setAddRackSaving] = useState(false)

  // Fetch rack and device data
  useEffect(() => {
    let cancelled = false
    async function load() {
      try {
        setLoading(true)
        setError(null)

        const [rackData, deviceData] = await Promise.all([
          api.get<Rack[] | Rack>(`/sites/${siteId}/racks`),
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          api.get<{ data: any[] } | any[]>(`/sites/${siteId}/devices?limit=200`),
        ])

        const rackList = Array.isArray(rackData) ? rackData : [rackData]

        // Merge device details into rack devices
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const deviceMap = new Map<string, any>()
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const devArr = Array.isArray(deviceData) ? deviceData : (deviceData as { data?: any[] })?.data || []
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        devArr.forEach((d: any) => deviceMap.set(d.id as string, d))

        for (const rack of rackList) {
          if (rack.devices) {
            rack.devices = rack.devices.map(rd => {
              const full = deviceMap.get(rd.device_id)
              if (!full) return rd
              // Merge enrichment from site_devices (status, ip, name) but preserve rack-specific fields
              return {
                ...rd,
                name: rd.name || full.label || full.hostname || '',
                status: full.status || rd.status,
                ip: full.ip_address || rd.ip,
                // Use spec_model from site_devices if rack API didn't provide model
                model: rd.model || full.spec_model || '',
                mpn: rd.mpn || full.spec_model || null,
                type: rd.type || full.spec_category || 'unknown',
              }
            })
          }
        }

        if (cancelled) return

        setRacks(rackList)

        // Fetch interfaces for switches and patch panels to build connections
        const connDevices = rackList
          .flatMap(r => r.devices || [])
          .filter(d => d.type === 'switch' || d.type === 'patch_panel')

        const allConns: RackConnection[] = []
        const allPortsMaps: Record<string, Record<number, PortMapEntry>> = {}

        // Build device list for shared buildPortsMap
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const allDevicesList = devArr.map((d: any) => ({
          id: d.id as string,
          label: d.label as string | undefined,
          name: (d.hostname || d.label) as string | undefined,
          ip_address: d.ip_address as string | undefined,
          status: d.status as string | undefined,
          category: d.spec_category as string | undefined,
          type: d.spec_category as string | undefined,
        }))

        await Promise.all(
          connDevices.map(async (dev) => {
            try {
              const ifaces = await api.get<DeviceInterface[]>(
                `/sites/${siteId}/devices/${dev.device_id || dev.id}/interfaces`
              )
              if (!Array.isArray(ifaces)) return

              // Build shared portsMap for this device
              const portsMap = buildPortsMap(
                ifaces.map(iface => ({
                  port_number: iface.port_number,
                  connected_device_id: iface.connected_device_id,
                  vlan_id: iface.vlan,
                  poe_draw: iface.poe_draw,
                  speed: iface.speed,
                  status: iface.status,
                })),
                allDevicesList
              )
              allPortsMaps[dev.id] = portsMap

              ifaces.forEach(iface => {
                if (iface.connected_device_id) {
                  allConns.push({
                    ppPort: iface.port_number,
                    swId: dev.id,
                    swPort: iface.port_number,
                    cable: iface.cable_id || `C-${String(iface.port_number).padStart(3, '0')}`,
                    device: iface.connected_device_id,
                    deviceName: iface.connected_device_name || iface.connected_device_id,
                    deviceType: iface.connected_device_type || 'camera',
                    ip: iface.ip || '',
                    vlan: iface.vlan,
                    speed: iface.speed,
                    poe: iface.poe_draw,
                    offline: iface.status === 'offline' || iface.status === 'down',
                  })
                }
              })
            } catch {
              // Interface fetch failed for this device, continue
            }
          })
        )

        if (!cancelled) {
          setConnections(allConns)
          setPortsMaps(allPortsMaps)
          setLoading(false)
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load rack data')
          setLoading(false)
        }
      }
    }

    load()
    return () => { cancelled = true }
  }, [siteId])

  // Index connections
  const { connByPP, connBySW } = useMemo(() => {
    const byPP: Record<number, RackConnection> = {}
    const bySW: Record<string, Record<number, RackConnection>> = {}
    connections.forEach(c => {
      byPP[c.ppPort] = c
      if (!bySW[c.swId]) bySW[c.swId] = {}
      bySW[c.swId][c.swPort] = c
    })
    return { connByPP: byPP, connBySW: bySW }
  }, [connections])

  const rack = racks[0] // Primary rack
  const devices = useMemo(() => rack?.devices || [], [rack?.devices])
  const totalU = rack?.total_u || 42
  const _rackH = totalU * U_H

  const zoomIn = useCallback((device: RackDevice) => {
    setAnimating(true)
    setTimeout(() => { setZoomed(device.id); setAnimating(false) }, 350)
  }, [])

  const zoomOut = useCallback(() => {
    setAnimating(true)
    setTimeout(() => { setZoomed(null); setAnimating(false) }, 250)
  }, [])

  const handlePortHover = useCallback((deviceId: string, portNum: number | string | null) => {
    setHovPort(portNum ? { deviceId, portNum } : null)
    if (!portNum) { setHovConn(null); return }
    if (typeof portNum === 'string') return // string ports (e.g. R940 drive bays) don't map to connections
    const device = devices.find(d => d.id === deviceId)
    if (device?.type === 'patch_panel') setHovConn(connByPP[portNum] || null)
    else if (device?.type === 'switch') setHovConn(connBySW[deviceId]?.[portNum] || null)
  }, [devices, connByPP, connBySW])

  const handlePortClick = useCallback((deviceId: string, portNum: number | string | null) => {
    if (!portNum || typeof portNum === 'string') return // string ports don't map to connections
    const device = devices.find(d => d.id === deviceId)
    let conn: RackConnection | null = null
    if (device?.type === 'patch_panel') conn = connByPP[portNum] || null
    else if (device?.type === 'switch') conn = connBySW[deviceId]?.[portNum] || null
    setSelConn(prev => prev === conn ? null : conn)
  }, [devices, connByPP, connBySW])

  // ── Add Rack Modal ────────────────────────────────────

  function renderAddRackModal() {
    return (
      <div style={{ position: 'fixed', inset: 0, zIndex: 50, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.4)' }} onClick={() => setShowAddRack(false)} />
        <div style={{ position: 'relative', background: 'var(--bg-surface)', borderRadius: 12, padding: 24, width: 400, boxShadow: 'var(--shadow-xl, 0 8px 24px rgba(0,0,0,0.15))' }}>
          <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 16 }}>Add Rack</h3>
          <form onSubmit={async (e) => {
            e.preventDefault()
            setAddRackSaving(true)
            try {
              await api.post(`/sites/${siteId}/racks-quick`, {
                name: addRackForm.name,
                total_units: addRackForm.total_u,
                type: addRackForm.type,
                description: addRackForm.location || undefined,
              })
              setShowAddRack(false)
              setAddRackForm({ name: '', total_u: 42, type: 'freestanding', location: '' })
              // Reload
              window.location.reload()
            } catch { toast('Failed to add rack', 'error') }
            finally { setAddRackSaving(false) }
          }}>
            <div style={{ marginBottom: 12 }}>
              <label style={{ fontSize: 12, fontWeight: 600, display: 'block', marginBottom: 4 }}>Name *</label>
              <input className="v3-input" required value={addRackForm.name} onChange={e => setAddRackForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. MDF Rack 1" />
            </div>
            <div className="flex gap-3" style={{ marginBottom: 12 }}>
              <div style={{ flex: 1 }}>
                <label style={{ fontSize: 12, fontWeight: 600, display: 'block', marginBottom: 4 }}>Total U</label>
                <input className="v3-input" type="number" min={1} max={48} value={addRackForm.total_u} onChange={e => setAddRackForm(f => ({ ...f, total_u: parseInt(e.target.value) || 42 }))} />
              </div>
              <div style={{ flex: 1 }}>
                <label style={{ fontSize: 12, fontWeight: 600, display: 'block', marginBottom: 4 }}>Type</label>
                <div className="v3-select-wrap">
                  <select className="v3-select" value={addRackForm.type} onChange={e => setAddRackForm(f => ({ ...f, type: e.target.value }))}>
                    <option value="freestanding">Freestanding</option>
                    <option value="wall_mount">Wall Mount</option>
                    <option value="open_frame">Open Frame</option>
                  </select>
                  <span className="v3-select-chevron"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6,9 12,15 18,9" /></svg></span>
                </div>
              </div>
            </div>
            <div style={{ marginBottom: 16 }}>
              <label style={{ fontSize: 12, fontWeight: 600, display: 'block', marginBottom: 4 }}>Location</label>
              <input className="v3-input" value={addRackForm.location} onChange={e => setAddRackForm(f => ({ ...f, location: e.target.value }))} placeholder="e.g. Server Room A" />
            </div>
            <div className="flex gap-2 justify-end">
              <Button type="button" variant="secondary" onClick={() => setShowAddRack(false)}>Cancel</Button>
              <Button type="submit" disabled={addRackSaving}>{addRackSaving ? 'Adding...' : 'Add Rack'}</Button>
            </div>
          </form>
        </div>
      </div>
    )
  }

  // ── Loading / Error ────────────────────────────────────

  if (loading) {
    return (
      <div style={S.root}>
        <style>{CSS}</style>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 300, color: 'var(--text-secondary, #9ca3af)' }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 14, fontFamily: 'var(--font-mono, monospace)', marginBottom: 8 }}>Loading rack data...</div>
            <div style={{ width: 40, height: 2, background: 'var(--accent, #c8850a)', borderRadius: 1, margin: '0 auto', animation: 'cablePulse 1.5s ease-in-out infinite' }} />
          </div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div style={S.root}>
        <style>{CSS}</style>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 300, color: 'var(--err)' }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 14, fontFamily: 'var(--font-mono, monospace)', marginBottom: 4 }}>Failed to load rack</div>
            <div style={{ fontSize: 12, color: 'var(--text-tertiary, #5c6370)' }}>{error}</div>
          </div>
        </div>
      </div>
    )
  }

  if (!rack) {
    return (
      <div style={S.root}>
        <style>{CSS}</style>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: 300, color: 'var(--text-tertiary, #5c6370)' }}>
          <div style={{ fontSize: 14, fontFamily: 'var(--font)', marginBottom: 12 }}>No racks found for this site.</div>
          <Button onClick={() => setShowAddRack(true)} icon={PlusIcon}>Add Rack</Button>
        </div>
        {showAddRack && renderAddRackModal()}
      </div>
    )
  }

  // ── Zoomed Device View ─────────────────────────────────

  if (zoomed) {
    const device = devices.find(d => d.id === zoomed)
    if (!device) { setZoomed(null); return null }

    const mod = getDeviceModule(device.mpn || device.model)
    // Build PortConnection map from shared portsMaps for renderFront
    const devicePM = portsMaps[device.id] || {}
    const conns: Record<number, PortConnection> = {}
    Object.entries(devicePM).forEach(([portStr, entry]) => {
      const portNum = Number(portStr)
      conns[portNum] = {
        deviceType: entry.deviceType,
        offline: entry.offline,
      }
    })
    // Fall back to connection-based data if portsMaps is empty
    if (Object.keys(conns).length === 0) {
      const fallbackConns = device.type === 'switch' ? (connBySW[device.id] || {}) : connByPP
      Object.entries(fallbackConns).forEach(([port, c]) => {
        conns[Number(port)] = c as unknown as PortConnection
      })
    }
    const poeData: Record<number, number> = {}
    if (device.type === 'switch') {
      Object.entries(devicePM).forEach(([port, entry]) => {
        poeData[Number(port)] = entry.poe ?? (entry.deviceType === 'camera' ? rnd(8, 25) : entry.deviceType === 'access' ? 6 : entry.deviceType === 'networking' ? 18 : 5)
      })
    }

    const selPortNum = selConn && device.type === 'switch' && connBySW[device.id]
      ? Number(Object.entries(connBySW[device.id]).find(([, c]) => c === selConn)?.[0]) || null
      : null

    return (
      <div style={S.root} className={animating ? '' : 'page-enter'}>
        <style>{CSS}</style>
        <div style={S.header}>
          <button onClick={zoomOut} className="action-btn" style={S.backBtn}>{'← Back to Rack'}</button>
          {onSwitchToNetwork && device.type === 'switch' && (
            <button onClick={() => onSwitchToNetwork(device.id)} className="action-btn" style={{ ...S.backBtn, marginLeft: 6 }}>{'Network View →'}</button>
          )}
          <div style={{ flex: 1, marginLeft: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ width: 8, height: 8, borderRadius: 4, background: getStatusColor(device.status) }} className={isStatusOnline(device.status) ? 'status-pulse' : ''} />
              <h2 style={{ fontSize: 20, fontWeight: 800, color: 'var(--text, #f0f1f3)' }}>{device.name}</h2>
              <span style={{ fontFamily: 'var(--font-mono, monospace)', fontSize: 12, color: 'var(--text-tertiary, #5c6370)' }}>{device.model}</span>
              <span className="live-badge" style={S.liveBadge}>{'● LIVE'}</span>
            </div>
            {device.ip && (
              <div style={{ fontFamily: 'var(--font-mono, monospace)', fontSize: 11, color: 'var(--text-tertiary, #5c6370)', marginTop: 2 }}>
                IP: {device.ip}{device.uptime ? ` · Up: ${device.uptime}` : ''}
              </div>
            )}
          </div>
        </div>

        {/* Large device render */}
        <div style={{ marginTop: 16 }}>
          {(() => {
            const svgModel = getModelForDevice(device)
            if (svgModel && PORT_CONFIGS[svgModel]) {
              const svgMod = makeUniFiSwitchModule(svgModel)
              const svgHtml = svgMod.renderFront()
              const portPositions = svgMod.getPortPositions()
              const { width: vbW, height: vbH } = svgMod.viewBox
              const activeHovPort = hovPort?.deviceId === device.id ? hovPort.portNum : null
              const deviceConns = connBySW[device.id] || {}

              const handleSvgPortHover = (portNum: number | null, e?: React.MouseEvent) => {
                handlePortHover(device.id, portNum)
                if (portNum && e) {
                  const conn = deviceConns[portNum] || null
                  const entry = devicePM[portNum] || null
                  const rect = (e.currentTarget as Element).getBoundingClientRect()
                  setPortTooltip({ portNum, x: rect.left + rect.width / 2, y: rect.top - 4, conn, entry })
                } else {
                  setPortTooltip(null)
                }
              }

              const handleSvgPortClick = (portNum: number) => {
                handlePortClick(device.id, portNum)
              }

              return (
                <div style={{ position: 'relative' }}>
                  {/* Photo-realistic SVG from UniFi factory */}
                  <div
                    dangerouslySetInnerHTML={{ __html: svgHtml }}
                    style={{ width: '100%', borderRadius: 10, overflow: 'hidden' }}
                  />
                  {/* Interactive port overlay */}
                  <svg
                    ref={svgOverlayRef}
                    viewBox={`0 0 ${vbW} ${vbH}`}
                    width="100%"
                    style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', borderRadius: 10 }}
                  >
                    {portPositions.map(pp => {
                      const conn = deviceConns[pp.index]
                      const isHov = activeHovPort === pp.index
                      const isSel = selPortNum === pp.index
                      return (
                        <rect
                          key={pp.index}
                          x={pp.x}
                          y={pp.y}
                          width={pp.width}
                          height={pp.height}
                          rx={pp.type === 'sfp' ? 1.1 : 0.9}
                          fill={isSel ? 'rgba(200,133,10,0.35)' : isHov ? 'rgba(200,133,10,0.2)' : 'transparent'}
                          stroke={isSel ? 'var(--accent)' : isHov ? 'rgba(200,133,10,0.5)' : 'transparent'}
                          strokeWidth={isSel ? 1.5 : isHov ? 1 : 0}
                          style={{ cursor: conn ? 'pointer' : 'default' }}
                          onMouseEnter={(e) => handleSvgPortHover(pp.index, e)}
                          onMouseLeave={() => handleSvgPortHover(null)}
                          onClick={() => handleSvgPortClick(pp.index)}
                        />
                      )
                    })}
                  </svg>
                  {/* Port tooltip */}
                  {portTooltip && (
                    <PortTooltip
                      portNum={portTooltip.portNum}
                      conn={portTooltip.conn}
                      entry={portTooltip.entry}
                      x={portTooltip.x}
                      y={portTooltip.y}
                    />
                  )}
                </div>
              )
            }
            // Fallback: existing generic device module rendering
            return (
              <svg viewBox={`0 0 ${DEVICE_W} ${U_H * device.size_u}`} width="100%" style={{ maxHeight: 200, borderRadius: 10, overflow: 'visible' }}>
                {mod.renderFront(DEVICE_W, U_H * device.size_u, {
                  ports: conns as Record<number, PortConnection>,
                  hovPort: hovPort?.deviceId === device.id ? hovPort.portNum : null,
                  selPort: selPortNum,
                  onHover: (p) => handlePortHover(device.id, p),
                  onClick: (p) => handlePortClick(device.id, p),
                  poeData,
                  device: { id: device.id, name: device.name, nameShort: device.name, model: device.model, status: device.status, ip: device.ip },
                })}
              </svg>
            )
          })()}
        </div>

        {/* Connection detail */}
        {(hovConn || selConn) && <ConnDetail conn={selConn || hovConn} />}

        {/* Port table */}
        <PortTable connections={connections.filter(c => c.swId === device.id)} deviceId={device.id} onSelect={setSelConn} selConn={selConn} hovPortNum={hovPort?.deviceId === device.id && typeof hovPort?.portNum === 'number' ? hovPort.portNum : null} />
      </div>
    )
  }

  // ── Rack Overview ──────────────────────────────────────
  // At overview scale, each device renders as a simplified bar — NOT the full SVG module.
  // This keeps the rack readable at small sizes where detailed port SVGs would be illegible.

  const BAR_H = 40 // Height per U for overview bars
  const BAR_W = RACK_W - 60
  const BAR_X = DEVICE_X
  const overviewH = totalU * BAR_H + 20

  return (
    <div style={S.root} className={animating ? '' : 'page-enter'}>
      <style>{CSS}</style>

      {/* Header */}
      <div style={S.header}>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <h2 style={{ fontSize: 20, fontWeight: 800, color: 'var(--text, #f0f1f3)' }}>{rack.name}</h2>
            <span style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-3)', background: 'var(--bg-3)', padding: '2px 6px', borderRadius: 4, fontFamily: 'var(--font-mono, monospace)' }}>{totalU}U</span>
            <button
              className="action-btn"
              onClick={() => {
                setEditRackForm({ name: rack.name, total_u: totalU, type: 'freestanding', location: rack.location || '' })
                setShowEditRack(true)
              }}
              style={{ padding: '3px 8px', borderRadius: 4, border: '1px solid var(--border-subtle, #2a2d35)', background: 'transparent', color: 'var(--text-tertiary, #5c6370)', fontSize: 11, cursor: 'pointer', fontFamily: 'var(--font-sans, sans-serif)' }}
              title="Edit rack"
            >Edit</button>
          </div>
          <div style={{ fontSize: 12, color: 'var(--text-tertiary, #5c6370)', marginTop: 2 }}>
            {rack.location}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          <button
            className={`face-btn ${face === 'front' ? 'active' : ''}`}
            style={S.faceBtn}
            onClick={() => setFace('front')}
          >
            Front
          </button>
          <button
            className={`face-btn ${face === 'back' ? 'active' : ''}`}
            style={S.faceBtn}
            onClick={() => setFace('back')}
          >
            Back
          </button>
        </div>
      </div>

      {/* Rack SVG — Overview mode with simplified device bars */}
      <div style={S.rackOuter} className="rack-chassis">
        <svg viewBox={`0 0 ${RACK_W} ${overviewH}`} width="100%" style={{ maxHeight: 600 }}>
          {/* Rack frame */}
          <rect x={0} y={0} width={RACK_W} height={overviewH} rx={6} fill="#08090c" stroke="var(--border-subtle, #2a2d35)" strokeWidth={1} />
          {/* Rail posts */}
          <rect x={8} y={5} width={14} height={overviewH - 10} rx={2} fill="#18181c" stroke="#222" strokeWidth={0.5} />
          <rect x={RACK_W - 22} y={5} width={14} height={overviewH - 10} rx={2} fill="#18181c" stroke="#222" strokeWidth={0.5} />

          {/* U markers + empty slot dashes */}
          {Array.from({ length: totalU }, (_, i) => {
            const occupied = devices.some(d => i + 1 >= d.start_u && i + 1 < d.start_u + d.size_u)
            return (
              <g key={`u-${i}`}>
                <text x={6} y={i * BAR_H + BAR_H / 2 + 12} fontSize={6} fill="#3a3d46" fontFamily="var(--font-mono, monospace)" fontWeight={700} textAnchor="middle">
                  {i + 1}U
                </text>
                {/* Empty slot outline */}
                {!occupied && (
                  <rect
                    x={BAR_X} y={i * BAR_H + 10} width={BAR_W} height={BAR_H - 2}
                    rx={2} fill="none" stroke="#1a1a1e" strokeWidth={0.4} strokeDasharray="4 4"
                  />
                )}
              </g>
            )
          })}

          {/* Device Bars — simplified overview rendering */}
          {devices.map(device => {
            const mod = getDeviceModule(device.mpn || device.model)
            const deviceY = (device.start_u - 1) * BAR_H + 10
            const deviceH = device.size_u * BAR_H - 2
            const isHov = hovDevice === device.id
            const statusColor = getStatusColor(device.status)

            // Port utilization data — use shared portsMaps for accurate counts
            const devicePortsMap = portsMaps[device.id] || {}
            const connectedCount = Object.keys(devicePortsMap).length
            const totalPorts = mod.frontPorts || device.ports || 24
            const hasSfp = (mod.sfpPorts || 0) > 0 || (mod.frontPorts || 0) > totalPorts

            return (
              <g
                key={device.id}
                onClick={() => zoomIn(device)}
                onMouseEnter={() => setHovDevice(device.id)}
                onMouseLeave={() => setHovDevice(null)}
                style={{ cursor: 'pointer' }}
              >
                {/* Device body */}
                <rect x={BAR_X} y={deviceY} width={BAR_W} height={deviceH} rx={3}
                  fill={isHov ? "#1e2228" : "#161820"}
                  stroke={isHov ? "rgba(200,133,10,0.31)" : "var(--border-h)"}
                  strokeWidth={isHov ? 1.5 : 1} />

                {/* Amber top edge highlight */}
                <rect x={BAR_X} y={deviceY} width={BAR_W} height={1.5} rx={1} fill="var(--accent)" opacity={0.3} />

                {/* Status LED */}
                <circle cx={BAR_X + 14} cy={deviceY + deviceH / 2} r={3} fill={statusColor} />

                {/* Device label */}
                <text x={BAR_X + 26} y={deviceY + deviceH / 2 + 1} fontSize={11} fill="var(--text-1)" fontFamily="JetBrains Mono, monospace" fontWeight={700}>{device.name}</text>

                {/* Model name */}
                <text x={BAR_X + 26 + Math.min((device.name?.length || 6) * 7, 100)} y={deviceY + deviceH / 2 + 1} fontSize={9} fill="var(--text-3)" fontFamily="JetBrains Mono, monospace">{device.model}</text>

                {/* Port utilization bar (mini) */}
                {totalPorts > 0 && (
                  <g transform={`translate(${BAR_X + 200}, ${deviceY + deviceH / 2 - 4})`}>
                    {Array.from({ length: Math.min(totalPorts, 24) }).map((_, i) => {
                      const portEntry = devicePortsMap[i + 1]
                      const isConnected = !!portEntry
                      const color = isConnected ? (TYPE_COLORS[portEntry?.deviceType || ''] || 'var(--accent)') : '#1e2028'
                      return <rect key={i} x={i * 5} y={0} width={4} height={8} rx={1} fill={color} opacity={isConnected ? 0.7 : 0.3} />
                    })}
                  </g>
                )}

                {/* Port count */}
                {totalPorts > 0 && (
                  <text x={BAR_X + BAR_W - 60} y={deviceY + deviceH / 2 + 1} fontSize={10} fill="var(--text-2)" fontFamily="JetBrains Mono, monospace" textAnchor="end">
                    {connectedCount}/{totalPorts}
                  </text>
                )}

                {/* SFP indicator */}
                {hasSfp && (
                  <text x={BAR_X + BAR_W - 16} y={deviceY + deviceH / 2 + 1} fontSize={7} fill="var(--text-3)" fontFamily="JetBrains Mono, monospace" textAnchor="end">SFP</text>
                )}

                {/* Click hint on hover */}
                {isHov && (
                  <text x={BAR_X + BAR_W - 8} y={deviceY + deviceH / 2 + 1} fontSize={10} fill="var(--accent)" textAnchor="end">→</text>
                )}
              </g>
            )
          })}
        </svg>
      </div>

      {/* Connection detail panel */}
      {(hovConn || selConn) && <ConnDetail conn={selConn || hovConn} />}

      {/* Edit Rack Modal */}
      {showEditRack && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 50, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.4)' }} onClick={() => setShowEditRack(false)} />
          <div style={{ position: 'relative', background: 'var(--bg-surface, #141519)', borderRadius: 12, padding: 24, width: 420, boxShadow: '0 25px 50px rgba(0,0,0,0.5)', border: '1px solid var(--border-subtle, #2a2d35)' }}>
            <h3 style={{ fontSize: 16, fontWeight: 700, color: 'var(--text, #f0f1f3)', marginBottom: 16 }}>Edit Rack</h3>
            <form onSubmit={async (e) => {
              e.preventDefault()
              setEditRackSaving(true)
              try {
                await api.put(`/racks/${rack.slug}`, {
                  name: editRackForm.name,
                  total_units: editRackForm.total_u,
                  type: editRackForm.type,
                })
                setShowEditRack(false)
                window.location.reload()
              } catch { toast('Failed to update rack', 'error') }
              finally { setEditRackSaving(false) }
            }}>
              <div style={{ marginBottom: 12 }}>
                <label style={{ fontSize: 12, fontWeight: 600, display: 'block', marginBottom: 4, color: 'var(--text-secondary, #9ca3af)' }}>Name *</label>
                <input className="v3-input" required value={editRackForm.name} onChange={e => setEditRackForm(f => ({ ...f, name: e.target.value }))} style={{ width: '100%', boxSizing: 'border-box' }} />
              </div>
              <div style={{ marginBottom: 12 }}>
                <label style={{ fontSize: 12, fontWeight: 600, display: 'block', marginBottom: 4, color: 'var(--text-secondary, #9ca3af)' }}>Location</label>
                <input className="v3-input" value={editRackForm.location} onChange={e => setEditRackForm(f => ({ ...f, location: e.target.value }))} placeholder="e.g. MDF Room 1" style={{ width: '100%', boxSizing: 'border-box' }} />
              </div>
              <div style={{ marginBottom: 12 }}>
                <label style={{ fontSize: 12, fontWeight: 600, display: 'block', marginBottom: 4, color: 'var(--text-secondary, #9ca3af)' }}>Total U</label>
                <div className="v3-select-wrap">
                  <select className="v3-select" value={editRackForm.total_u} onChange={e => setEditRackForm(f => ({ ...f, total_u: parseInt(e.target.value) || 42 }))} style={{ width: '100%', boxSizing: 'border-box' }}>
                    {[4, 6, 9, 12, 15, 18, 22, 25, 42, 45, 48].map(u => (
                      <option key={u} value={u}>{u}U</option>
                    ))}
                  </select>
                  <span className="v3-select-chevron"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6,9 12,15 18,9" /></svg></span>
                </div>
              </div>
              <div style={{ marginBottom: 16 }}>
                <label style={{ fontSize: 12, fontWeight: 600, display: 'block', marginBottom: 4, color: 'var(--text-secondary, #9ca3af)' }}>Type</label>
                <div className="v3-select-wrap">
                  <select className="v3-select" value={editRackForm.type} onChange={e => setEditRackForm(f => ({ ...f, type: e.target.value }))} style={{ width: '100%', boxSizing: 'border-box' }}>
                    <option value="freestanding">Floor Standing</option>
                    <option value="wall_mount">Wall Mount</option>
                    <option value="open_frame">Open Frame</option>
                    <option value="enclosed">Enclosed</option>
                  </select>
                  <span className="v3-select-chevron"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6,9 12,15 18,9" /></svg></span>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                <button type="button" onClick={() => setShowEditRack(false)} className="action-btn" style={{ ...S.backBtn }}>Cancel</button>
                <Button type="submit" disabled={editRackSaving} size="sm">{editRackSaving ? 'Saving...' : 'Save Changes'}</Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}

// ── Connection Detail Panel ──────────────────────────────

function ConnDetail({ conn }: { conn: RackConnection | null }) {
  if (!conn) return null
  const color = TYPE_COLORS[conn.deviceType] || 'var(--accent)'
  const vlan = conn.vlan ?? (conn.deviceType === 'camera' ? 104 : conn.deviceType === 'access' ? 105 : 1)

  return (
    <div style={S.connDetail} className="drawer-enter">
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
        <span style={{ width: 8, height: 8, borderRadius: 4, background: color }} />
        <span style={{ fontFamily: 'var(--font-mono, monospace)', fontWeight: 700, color: 'var(--accent, #c8850a)', fontSize: 13 }}>
          {conn.device}
        </span>
        <span style={{ color: 'var(--text-secondary, #9ca3af)', fontSize: 12 }}>{conn.deviceName}</span>
        {conn.offline && (
          <span style={{ fontSize: 10, color: 'var(--err)', fontWeight: 700 }}>{'● OFFLINE'}</span>
        )}
      </div>

      {/* Cable trace visualization */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 0, padding: '6px 0' }}>
        <TraceNode label={`${conn.swId.toUpperCase()}:P${conn.swPort}`} color={TYPE_COLORS.networking} />
        <TraceLine color={conn.offline ? 'var(--err)' : 'var(--accent)'} label={conn.cable} alive={!conn.offline} />
        <TraceNode label={`PP-1:P${conn.ppPort}`} color="var(--color-power)" />
        <TraceLine color={conn.offline ? 'var(--err)' : color} alive={!conn.offline} />
        <TraceNode label={conn.device} color={conn.offline ? 'var(--err)' : color} />
      </div>

      {/* Connection metadata */}
      <div style={{ fontFamily: 'var(--font-mono, monospace)', fontSize: 10, color: 'var(--text-tertiary, #5c6370)', marginTop: 4 }}>
        IP: {conn.ip || 'N/A'} · Cable: {conn.cable} · VLAN: {vlan}
        {conn.speed ? ` · Speed: ${conn.speed}` : ''}
        {conn.poe ? ` · PoE: ${conn.poe}W` : ''}
      </div>

      {/* Offline warning */}
      {conn.offline && (
        <div style={{ marginTop: 8, padding: '6px 10px', background: 'rgba(220,38,38,0.08)', border: '1px solid rgba(220,38,38,0.2)', borderRadius: 6 }}>
          <div style={{ fontSize: 11, color: 'var(--err)', fontWeight: 600 }}>{'⚠ Connection Offline'}</div>
          <div style={{ fontSize: 10, color: 'var(--text-tertiary, #5c6370)', marginTop: 2 }}>
            Check cable {conn.cable} between patch panel port {conn.ppPort} and endpoint device {conn.device}. Verify physical connection and PoE delivery.
          </div>
        </div>
      )}
    </div>
  )
}

// ── Trace Visualization Helpers ──────────────────────────

function TraceNode({ label, color }: { label: string; color: string }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, minWidth: 44 }}>
      <div style={{ width: 10, height: 10, borderRadius: 5, background: color }} />
      <div style={{ fontSize: 8, color: 'var(--text-secondary, #9ca3af)', textAlign: 'center', whiteSpace: 'nowrap' }}>{label}</div>
    </div>
  )
}

function TraceLine({ color, label, alive }: { color: string; label?: string; alive: boolean }) {
  return (
    <div style={{ flex: 1, minWidth: 20, display: 'flex', flexDirection: 'column', alignItems: 'stretch' }}>
      <div style={{ height: 2, borderRadius: 1, background: color, position: 'relative', overflow: 'hidden' }}>
        {alive && (
          <div style={{
            position: 'absolute', top: 0, left: 0, width: '30%', height: '100%',
            background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.4), transparent)',
            animation: 'cablePulse 1.5s ease-in-out infinite',
          }} />
        )}
      </div>
      {label && (
        <div style={{ fontFamily: 'var(--font-mono, monospace)', fontSize: 6, color: 'var(--text-disabled, #3a3d46)', textAlign: 'center', marginTop: 1 }}>
          {label}
        </div>
      )}
    </div>
  )
}

// ── Port Table ───────────────────────────────────────────

function PortTable({
  connections,
  deviceId: _deviceId,
  onSelect,
  selConn,
  hovPortNum,
}: {
  connections: RackConnection[]
  deviceId: string
  onSelect: (conn: RackConnection | null) => void
  selConn: RackConnection | null
  hovPortNum?: number | null
}) {
  if (!connections?.length) return null

  return (
    <div style={{ marginTop: 16 }}>
      <h3 style={{
        fontSize: 10, fontWeight: 700, color: 'var(--text-tertiary, #5c6370)',
        textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6,
      }}>
        Connected Devices
      </h3>
      <div style={{
        background: 'var(--bg-surface, #141519)', border: '1px solid var(--border-subtle, #1e2028)',
        borderRadius: 10, overflow: 'hidden',
      }}>
        <table className="v3-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              {['PORT', 'DEVICE', 'NAME', 'IP', 'CABLE', 'PP PORT', 'STATUS'].map(h => (
                <th key={h} style={{
                  fontSize: 8, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em',
                  color: 'var(--text-disabled, #3a3d46)', padding: '6px 10px',
                  borderBottom: '1px solid var(--border-subtle, #2a2d35)', textAlign: 'left',
                }}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {connections.map((c, i) => (
              <tr
                key={`${c.cable}-${i}`}
                className="table-row-enter"
                style={{
                  animationDelay: `${i * 25}ms`,
                  cursor: 'pointer',
                  background: selConn === c ? 'rgba(200,133,10,0.06)' : hovPortNum === c.swPort ? 'rgba(200,133,10,0.04)' : undefined,
                }}
                onClick={() => onSelect(selConn === c ? null : c)}
              >
                <td style={S.td}>
                  <span style={{ fontFamily: 'var(--font-mono, monospace)', fontWeight: 700, color: 'var(--text, #f0f1f3)' }}>
                    {c.swPort}
                  </span>
                </td>
                <td style={S.td}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <span style={{ width: 5, height: 5, borderRadius: 3, background: TYPE_COLORS[c.deviceType] || 'var(--accent)' }} />
                    <span style={{ fontFamily: 'var(--font-mono, monospace)', color: 'var(--accent, #c8850a)', fontWeight: 600, fontSize: 10 }}>
                      {c.device}
                    </span>
                  </span>
                </td>
                <td style={S.td}>{c.deviceName}</td>
                <td style={S.td}>
                  <span style={{ fontFamily: 'var(--font-mono, monospace)', fontSize: 10, color: 'var(--text-tertiary, #5c6370)' }}>
                    {c.ip || 'N/A'}
                  </span>
                </td>
                <td style={S.td}>
                  <span style={{ fontFamily: 'var(--font-mono, monospace)', fontSize: 9, color: 'var(--color-power)' }}>{c.cable}</span>
                </td>
                <td style={S.td}>
                  <span style={{ fontFamily: 'var(--font-mono, monospace)', fontSize: 9, color: 'var(--color-power)' }}>PP1:{c.ppPort}</span>
                </td>
                <td style={S.td}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                    <span
                      className={c.offline ? 'status-pulse-red' : 'status-pulse'}
                      style={{ width: 5, height: 5, borderRadius: 3, background: c.offline ? 'var(--err)' : 'var(--ok)' }}
                    />
                    <span style={{ fontSize: 9, color: c.offline ? 'var(--err)' : 'var(--ok)' }}>
                      {c.offline ? 'Down' : 'Up'}
                    </span>
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

// ── Port Tooltip ─────────────────────────────────────────

function PortTooltip({ portNum, conn, entry, x, y }: {
  portNum: number
  conn: RackConnection | null
  entry: PortMapEntry | null
  x: number
  y: number
}) {
  const name = conn?.deviceName || entry?.name || entry?.label || null
  const ip = conn?.ip || entry?.ip || null
  const offline = conn?.offline ?? entry?.offline ?? false
  const statusLabel = offline ? 'Offline' : 'Online'
  const statusColor = offline ? 'var(--err)' : 'var(--ok)'

  return (
    <div
      className="drawer-enter"
      style={{
        position: 'fixed',
        left: x,
        top: y,
        transform: 'translate(-50%, -100%)',
        background: 'var(--bg-surface, #141519)',
        border: '1px solid var(--border, #2a2d35)',
        borderRadius: 8,
        padding: '6px 10px',
        pointerEvents: 'none',
        zIndex: 100,
        minWidth: 120,
        boxShadow: '0 8px 24px rgba(0,0,0,0.5)',
      }}
    >
      <div style={{ fontFamily: 'var(--font-mono, monospace)', fontSize: 11, fontWeight: 700, color: 'var(--accent, #c8850a)', marginBottom: 2 }}>
        Port {portNum}
      </div>
      {name && (
        <div style={{ fontSize: 10, color: 'var(--text, #f0f1f3)', marginBottom: 1 }}>{name}</div>
      )}
      {ip && (
        <div style={{ fontFamily: 'var(--font-mono, monospace)', fontSize: 9, color: 'var(--text-tertiary, #5c6370)' }}>{ip}</div>
      )}
      {(conn || entry) && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 3, marginTop: 2 }}>
          <span style={{ width: 5, height: 5, borderRadius: 3, background: statusColor }} />
          <span style={{ fontSize: 9, color: statusColor, fontWeight: 600 }}>{statusLabel}</span>
        </div>
      )}
      {!conn && !entry && (
        <div style={{ fontSize: 9, color: 'var(--text-disabled, #3a3d46)' }}>No connection</div>
      )}
    </div>
  )
}

// ── CSS Animations ───────────────────────────────────────

const CSS = `
  @keyframes pulseGreen { 0%,100% { box-shadow: 0 0 0 0 rgba(5,150,105,0.4); } 50% { box-shadow: 0 0 0 4px rgba(5,150,105,0); } }
  @keyframes pulseRed { 0%,100% { box-shadow: 0 0 0 0 rgba(220,38,38,0.5); } 50% { box-shadow: 0 0 0 5px rgba(220,38,38,0); } }
  .status-pulse { animation: pulseGreen 3s ease-in-out infinite; border-radius: 50%; }
  .status-pulse-red { animation: pulseRed 1.5s ease-in-out infinite; border-radius: 50%; }

  .page-enter { animation: pageIn 0.4s cubic-bezier(0.16,1,0.3,1); }
  @keyframes pageIn { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }

  .drawer-enter { animation: drawIn 0.3s cubic-bezier(0.34,1.56,0.64,1); }
  @keyframes drawIn { from { opacity: 0; transform: translateY(6px) scale(0.98); } to { opacity: 1; transform: translateY(0) scale(1); } }

  .table-row-enter { animation: rowIn 0.25s cubic-bezier(0.16,1,0.3,1) backwards; }
  @keyframes rowIn { from { opacity: 0; transform: translateX(-6px); } to { opacity: 1; transform: translateX(0); } }
  table tbody tr { transition: background 0.1s; }
  table tbody tr:hover { background: var(--bg-surface, #1e2028); }

  @keyframes cablePulse { 0% { transform: translateX(-100%); } 100% { transform: translateX(300%); } }

  .live-badge { animation: livePulse 2s ease-in-out infinite; }
  @keyframes livePulse { 0%,100% { opacity: 1; } 50% { opacity: 0.6; } }

  .rack-chassis { transition: box-shadow 0.3s; }
  .rack-chassis:hover { box-shadow: 0 0 80px rgba(200,133,10,0.03), 0 8px 40px rgba(0,0,0,0.3); }

  .action-btn { transition: all 0.15s cubic-bezier(0.16,1,0.3,1); }
  .action-btn:hover { border-color: rgba(200,133,10,0.25); color: var(--text, #f0f1f3); transform: translateY(-1px); }
  .action-btn:active { transform: scale(0.97); }

  .face-btn { transition: all 0.15s; }
  .face-btn:hover { border-color: rgba(200,133,10,0.25); color: var(--text, #f0f1f3); }
  .face-btn.active { background: rgba(200,133,10,0.12); border-color: rgba(200,133,10,0.3); color: var(--accent, #c8850a); }

  @media (prefers-reduced-motion: reduce) { *, *::before, *::after { animation-duration: 0.01ms !important; transition-duration: 0.01ms !important; } }
`

// ── Inline Styles ────────────────────────────────────────

const S: Record<string, React.CSSProperties> = {
  root: {
    fontFamily: 'var(--font-sans, sans-serif)',
    background: 'var(--bg-base, #0c0d10)',
    color: 'var(--text-secondary, #9ca3af)',
    minHeight: '100vh',
    padding: 24,
    maxWidth: 700,
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 14,
  },
  liveBadge: {
    fontSize: 9,
    fontWeight: 700,
    color: 'var(--ok)',
    background: 'rgba(5,150,105,0.12)',
    padding: '2px 7px',
    borderRadius: 5,
  },
  backBtn: {
    padding: '6px 14px',
    borderRadius: 6,
    border: '1px solid var(--border-subtle, #2a2d35)',
    background: 'transparent',
    color: 'var(--text-secondary, #9ca3af)',
    fontSize: 11,
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: 'var(--font-sans, sans-serif)',
  },
  faceBtn: {
    padding: '5px 12px',
    borderRadius: 6,
    border: '1px solid var(--border-subtle, #2a2d35)',
    background: 'transparent',
    color: 'var(--text-tertiary, #5c6370)',
    fontSize: 11,
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: 'var(--font-sans, sans-serif)',
  },
  rackOuter: {
    background: 'var(--bg-base, #0a0a0e)',
    border: '1px solid var(--border-subtle, #1e2028)',
    borderRadius: 12,
    padding: 12,
    marginBottom: 16,
  },
  connDetail: {
    background: 'var(--bg-surface, #141519)',
    border: '1px solid var(--border, #2a2d35)',
    borderRadius: 10,
    padding: 14,
    marginBottom: 12,
  },
  summary: {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
  },
  summaryItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '8px 12px',
    background: 'var(--bg-surface, #141519)',
    border: '1px solid var(--border-subtle, #1e2028)',
    borderRadius: 8,
  },
  td: {
    fontSize: 11,
    color: 'var(--text-secondary, #9ca3af)',
    padding: '6px 10px',
    borderBottom: '1px solid var(--border-subtle, #1e2028)',
  },
}
